import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Sparkles, Users, Heart, Shield, LifeBuoy, Globe2, ChevronRight } from 'lucide-react';
import { Heading, SectionLabel, Hairline } from '../components/ui-bits';
import CTABanner from '../components/CTABanner';
import ContactForm from '../components/ContactForm';
import { LogoMark } from '../components/Logo';
import { DESTINATIONS_INDIA, DESTINATIONS_WORLD, WHY_POINTS, RESPONSIBILITY, SERVICES, CERTIFIED_LEVELS, CERTIFIED_STANDARDS, HOW_IT_WORKS, MIRROR_AI_COUPLE, MIRROR_AI_TEAM, REAL_WEDDINGS, TESTIMONIALS, PRESS, HERO_IMAGE } from '../data/mock';

/* --- HERO --- */
const Hero = () => (
  <section className="relative h-[100vh] min-h-[720px] text-[#faf4ea] overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_IMAGE} alt="Destination wedding" className="w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#053e49]/70 via-[#053e49]/50 to-[#053e49]/90" />
      <div className="absolute inset-0 vignette" />
    </div>
    <div className="relative h-full flex flex-col justify-end pb-24 md:pb-28 max-w-[1440px] mx-auto px-6 md:px-10">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-7">
          <span className="block w-10 h-px bg-[#c3a66d]" />
          <span className="eyebrow text-[#c3a66d]">Sunday Mirror — One Promise</span>
        </div>
        <h1 className="serif-hero text-[52px] sm:text-6xl lg:text-[88px] leading-[0.98]">
          Your Wedding.
          <br/>
          <span className="italic text-[#ffeabf]">Anywhere in the World.</span>
        </h1>
        <p className="mt-7 max-w-xl text-[#e4ddc8] text-lg leading-relaxed">
          From finding the perfect destination to welcoming the last guest home, Sunday Mirror brings every part of your wedding together under one trusted team.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-[#d8d0bf]">
          <span>Destination Weddings</span><span className="text-[#c3a66d]">•</span>
          <span>Sunday Mirror Certified</span><span className="text-[#c3a66d]">•</span>
          <span>Guest Hospitality</span><span className="text-[#c3a66d]">•</span>
          <span>Complete Execution</span>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/plan" className="btn-gold">Start Planning Your Wedding <ArrowRight size={16} /></Link>
          <Link to="/destinations" className="btn-ghost">Explore Destinations</Link>
        </div>
      </div>
    </div>
    <div className="absolute bottom-6 right-6 md:right-10 eyebrow text-[#c3a66d]/80 hidden md:block">Scroll ↓</div>
  </section>
);

/* --- TRUST --- */
const Trust = () => (
  <section className="bg-[#faf4ea] py-24 md:py-32">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
      <SectionLabel align="center">The Sunday Mirror Promise</SectionLabel>
      <Heading className="mt-6 font-serif">One wedding. One team.<br/><span className="italic">Everything taken care of.</span></Heading>
      <div className="hairline mt-10 max-w-xs mx-auto" />
      <p className="mt-10 text-[#304247] text-lg leading-[1.9] max-w-3xl mx-auto">
        A destination wedding shouldn’t mean coordinating dozens of vendors from hundreds or thousands of kilometres away. With Sunday Mirror, you have <em className="font-serif text-[#053e49]">one team</em> responsible for the complete experience. We find the destination. We shortlist the venue. We build the team. We manage the guests. We coordinate the details. And when the celebration begins, we make sure you can actually enjoy it.
      </p>
      <p className="mt-10 font-serif italic text-2xl md:text-3xl text-[#053e49]">You celebrate. We take care of everything else.</p>
    </div>
  </section>
);

/* --- RESPONSIBILITY --- */
const Responsibility = () => (
  <section className="relative bg-[#053e49] text-[#faf4ea] py-28 overflow-hidden">
    <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage:'radial-gradient(circle at 10% 10%, #c3a66d, transparent 40%), radial-gradient(circle at 90% 80%, #c3a66d, transparent 50%)'}} />
    <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-5">
          <SectionLabel dark>Your Wedding, Our Responsibility</SectionLabel>
          <Heading dark className="mt-5 font-serif">From “Where?”<br/><span className="italic text-[#ffeabf]">to “I do.”</span></Heading>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[#d8d0bf] text-lg leading-relaxed">
            Whether you’re dreaming of a palace in Rajasthan, a beach in Kerala, sunset in Santorini, a celebration in Dubai or a weekend in Thailand, Sunday Mirror turns the idea into a professionally managed wedding experience.
          </p>
        </div>
      </div>

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#0b5260]">
        {RESPONSIBILITY.map((r) => (
          <div key={r.num} className="bg-[#053e49] p-8 group hover:bg-[#042f38] transition-colors">
            <div className="font-serif text-5xl text-[#c3a66d]">{r.num}</div>
            <h3 className="mt-6 font-serif text-2xl text-[#ffeabf]">{r.title}</h3>
            <p className="mt-3 text-sm text-[#a8b7bb] leading-relaxed">{r.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --- WHY --- */
const Why = () => {
  const icons = [Shield, Sparkles, Globe2, Users, LifeBuoy, Heart];
  return (
    <section className="bg-[#faf4ea] py-28">
      <div className="max-w-[1240px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <SectionLabel>Why Sunday Mirror</SectionLabel>
          <Heading className="mt-5 font-serif">Because destination weddings<br/><span className="italic">need more than a planner.</span></Heading>
          <p className="mt-6 text-[#304247] text-lg">They need a partner.</p>
        </div>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {WHY_POINTS.map((w, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={w.title} className="group">
                <div className="w-12 h-12 border border-[#c3a66d] flex items-center justify-center text-[#a6894f] group-hover:bg-[#c3a66d] group-hover:text-[#faf4ea] transition-colors"><Icon size={20} /></div>
                <h3 className="mt-5 font-serif text-2xl text-[#053e49]">{w.title}</h3>
                <div className="hairline mt-4 w-16" />
                <p className="mt-4 text-[#4a5c60] leading-relaxed text-[15px]">{w.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* --- DESTINATIONS --- */
const Destinations = () => (
  <section className="bg-[#fbf8f2] py-28">
    <div className="max-w-[1340px] mx-auto px-6 md:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div className="max-w-2xl">
          <SectionLabel>Destinations</SectionLabel>
          <Heading className="mt-5 font-serif">Where will <span className="italic">your story</span> take you?</Heading>
        </div>
        <Link to="/destinations" className="link-underline eyebrow text-[#a6894f] self-start lg:self-auto">View all destinations →</Link>
      </div>

      <h3 className="mt-14 font-serif text-3xl text-[#053e49]">India</h3>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {DESTINATIONS_INDIA.map((d) => (
          <Link to="/destinations" key={d.name} className="group block">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#053e49]">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#053e49] via-[#053e49]/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-[#faf4ea]">
                <div className="eyebrow text-[#c3a66d]">{d.tag}</div>
                <div className="font-serif text-2xl mt-1">{d.name}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <h3 className="mt-16 font-serif text-3xl text-[#053e49]">Around the World</h3>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {DESTINATIONS_WORLD.slice(0, 5).map((d) => (
          <Link to="/destinations" key={d.name} className="group">
            <div className="relative aspect-square overflow-hidden">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#053e49]/20 group-hover:bg-[#053e49]/40 transition-colors" />
            </div>
            <div className="mt-3">
              <div className="font-serif text-xl text-[#053e49]">{d.name}</div>
              <div className="text-xs text-[#6b7c80] mt-1">{d.country}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-14 text-center">
        <Link to="/destinations" className="btn-outline-dark">Find My Destination <ArrowRight size={16} /></Link>
      </div>
    </div>
  </section>
);

/* --- DESTINATION FINDER --- */
const Finder = () => (
  <section className="relative py-28 overflow-hidden">
    <div className="absolute inset-0">
      <img src="https://images.unsplash.com/photo-1731585926373-3bc511338cc0" alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#053e49]/85" />
    </div>
    <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <SectionLabel dark>Destination Finder</SectionLabel>
        <Heading dark className="mt-5 font-serif">Not sure where to <span className="italic text-[#ffeabf]">get married?</span></Heading>
        <p className="mt-6 text-[#d8d0bf] leading-relaxed">Tell us what you know. We’ll recommend destinations suited to your celebration.</p>
        <Link to="/plan" className="btn-gold mt-8">Find My Wedding Destination <ArrowRight size={16} /></Link>
      </div>
      <div className="grid grid-cols-2 gap-x-10 gap-y-6 text-[#faf4ea]">
        {['Your guest count','Preferred season','Approximate budget','Wedding style','Where guests will travel from','What matters most'].map((t) => (
          <div key={t} className="flex items-start gap-3"><span className="text-[#c3a66d] mt-1"><Compass size={16} /></span><span className="text-[15px]">{t}</span></div>
        ))}
      </div>
    </div>
  </section>
);

/* --- SERVICES --- */
const ServicesSec = () => (
  <section className="bg-[#faf4ea] py-28">
    <div className="max-w-[1340px] mx-auto px-6 md:px-10">
      <div className="text-center max-w-3xl mx-auto">
        <SectionLabel align="center">Services</SectionLabel>
        <Heading className="mt-5 font-serif">Everything <span className="italic">wedding.</span></Heading>
      </div>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-[#e8e0d2] border border-[#e8e0d2]">
        {SERVICES.map((s) => (
          <div key={s.title} className="bg-[#faf4ea] p-7 hover:bg-white transition-colors">
            <h3 className="font-serif text-xl text-[#053e49]">{s.title}</h3>
            <div className="hairline mt-3 w-10" />
            <ul className="mt-4 space-y-2">{s.items.map((i) => (<li key={i} className="text-[13.5px] text-[#4a5c60]">· {i}</li>))}</ul>
          </div>
        ))}
      </div>
      <div className="text-center mt-14">
        <Link to="/services" className="btn-outline-dark">All Services <ArrowRight size={16} /></Link>
      </div>
    </div>
  </section>
);

/* --- FIND YOUR TEAM --- */
const FindTeam = () => (
  <section className="bg-[#fbf8f2] py-28">
    <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-6 relative">
        <div className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1529636798458-92182e662485?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="aspect-[4/5] object-cover" />
          <img src="https://images.unsplash.com/photo-1561848355-890d054dc55a?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="aspect-[4/5] object-cover mt-10" />
        </div>
      </div>
      <div className="lg:col-span-6">
        <SectionLabel>Find Your Wedding Team</SectionLabel>
        <Heading className="mt-5 font-serif">Great weddings are built by <span className="italic">great people.</span></Heading>
        <p className="mt-6 text-[#4a5c60] leading-relaxed">Discover professionals selected for their craft, experience and service standards.</p>
        <div className="mt-8 flex flex-wrap gap-2">
          {['Photography','Beauty','Décor','Entertainment','Styling','Choreography','Hospitality','Transport','Content Creation'].map((t) => (
            <span key={t} className="px-3 py-1.5 text-xs tracking-wide border border-[#c3a66d]/50 text-[#053e49] hover:bg-[#053e49] hover:text-[#ffeabf] hover:border-[#053e49] transition-colors cursor-pointer">{t}</span>
          ))}
        </div>
        <div className="mt-8 p-5 border border-[#e8d2a0] bg-[#f6ead2]/60">
          <div className="eyebrow text-[#a6894f]">Look for</div>
          <div className="font-serif text-xl text-[#053e49] mt-1">Sunday Mirror Certified</div>
          <p className="text-sm text-[#4a5c60] mt-2">A visible standard for professionals who have completed our verification, destination-readiness and service-quality process.</p>
        </div>
        <Link to="/team" className="btn-outline-dark mt-8">Find Your Team <ArrowRight size={16} /></Link>
      </div>
    </div>
  </section>
);

/* --- CERTIFIED --- */
const Certified = () => (
  <section className="relative bg-[#053e49] text-[#faf4ea] py-28 overflow-hidden">
    <div className="absolute top-10 right-10 opacity-[0.07]"><LogoMark size={360} /></div>
    <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
      <div className="max-w-3xl">
        <SectionLabel dark>Sunday Mirror Certified</SectionLabel>
        <Heading dark className="mt-5 font-serif">Trust, <span className="italic text-[#ffeabf]">verified before the wedding.</span></Heading>
        <p className="mt-6 text-[#d8d0bf] leading-relaxed">A beautiful portfolio is not enough. Every professional carrying the Sunday Mirror Certified mark passes a structured review built for destination-wedding delivery.</p>
      </div>
      <div className="mt-14 grid md:grid-cols-2 gap-12">
        <div>
          <div className="eyebrow text-[#c3a66d]">The Standard</div>
          <ul className="mt-6 space-y-3">{CERTIFIED_STANDARDS.map((s) => (<li key={s} className="flex gap-3 text-[#e4ddc8]"><span className="text-[#c3a66d] mt-1.5">—</span><span>{s}</span></li>))}</ul>
        </div>
        <div>
          <div className="eyebrow text-[#c3a66d]">Quality that earns its level</div>
          <div className="mt-6 space-y-6">{CERTIFIED_LEVELS.map((l, i) => (
            <div key={l.level} className="border-l border-[#c3a66d] pl-5">
              <div className="flex items-baseline gap-3"><span className="font-serif text-[#c3a66d] text-xl">0{i+1}</span><h4 className="font-serif text-xl text-[#ffeabf]">{l.level}</h4></div>
              <p className="mt-2 text-sm text-[#a8b7bb] leading-relaxed">{l.body}</p>
            </div>
          ))}</div>
        </div>
      </div>
      <div className="mt-12"><Link to="/certified" className="btn-gold">Explore Certified Professionals <ArrowRight size={16} /></Link></div>
    </div>
  </section>
);

/* --- HOW IT WORKS --- */
const HowItWorks = () => (
  <section className="bg-[#fbf8f2] py-28">
    <div className="max-w-[1240px] mx-auto px-6 md:px-10">
      <div className="text-center max-w-2xl mx-auto">
        <SectionLabel align="center">How It Works</SectionLabel>
        <Heading className="mt-5 font-serif">Your wedding <span className="italic">in six steps.</span></Heading>
      </div>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
        {HOW_IT_WORKS.map((s) => (
          <div key={s.num} className="relative">
            <div className="flex items-baseline gap-4">
              <span className="font-serif text-6xl text-[#c3a66d]">{s.num}</span>
              <h3 className="font-serif text-2xl text-[#053e49]">{s.title}</h3>
            </div>
            <div className="hairline mt-5" />
            <p className="mt-4 text-[#4a5c60] leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
      <div className="text-center mt-14"><Link to="/plan" className="btn-outline-dark">Start With Step One <ArrowRight size={16} /></Link></div>
    </div>
  </section>
);

/* --- MIRROR AI --- */
const MirrorAI = () => (
  <section className="relative bg-[#042f38] text-[#faf4ea] py-28 overflow-hidden">
    <div className="absolute inset-0 opacity-[0.08]" style={{background:'radial-gradient(circle at 70% 30%, #c3a66d 0%, transparent 50%)'}} />
    <div className="relative max-w-[1240px] mx-auto px-6 md:px-10">
      <div className="flex items-center gap-4"><LogoMark size={42} /><span className="eyebrow text-[#c3a66d]">Mirror AI — Coming Soon</span></div>
      <Heading dark className="mt-6 font-serif max-w-3xl">Intelligence for the planning.<br/><span className="italic text-[#ffeabf]">Humans for the wedding.</span></Heading>
      <p className="mt-6 max-w-2xl text-[#d8d0bf] leading-relaxed">Mirror AI is Sunday Mirror’s future planning assistant—designed to make destination-wedding planning clearer, faster and better organised while your dedicated human team remains in control.</p>
      <div className="mt-14 grid md:grid-cols-2 gap-10">
        <div className="border border-[#0b5260] p-8">
          <div className="eyebrow text-[#c3a66d]">For Couples</div>
          <ul className="mt-5 space-y-2">{MIRROR_AI_COUPLE.map((i) => <li key={i} className="text-[#e4ddc8]">· {i}</li>)}</ul>
        </div>
        <div className="border border-[#0b5260] p-8">
          <div className="eyebrow text-[#c3a66d]">For The Sunday Mirror Team</div>
          <ul className="mt-5 space-y-2">{MIRROR_AI_TEAM.map((i) => <li key={i} className="text-[#e4ddc8]">· {i}</li>)}</ul>
        </div>
      </div>
      <div className="mt-10 p-6 border-l-2 border-[#c3a66d] bg-[#053e49]/40 max-w-3xl">
        <p className="font-serif italic text-xl text-[#ffeabf]">Mirror AI supports your planner. It never replaces one.</p>
      </div>
      <Link to="/mirror-ai" className="btn-gold mt-10">Learn About Mirror AI <ArrowRight size={16} /></Link>
    </div>
  </section>
);

/* --- REAL WEDDINGS --- */
const RealWeddings = () => (
  <section className="bg-[#faf4ea] py-28">
    <div className="max-w-[1340px] mx-auto px-6 md:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <SectionLabel>Real Weddings</SectionLabel>
          <Heading className="mt-5 font-serif">Real people. Real places.<br/><span className="italic">Remarkable celebrations.</span></Heading>
        </div>
        <Link to="/real-weddings" className="link-underline eyebrow text-[#a6894f]">View all →</Link>
      </div>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {REAL_WEDDINGS.slice(0, 3).map((w) => (
          <Link to="/real-weddings" key={w.couple} className="group block">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={w.image} alt={w.couple} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#053e49]/90 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 text-[#faf4ea]">
                <div className="eyebrow text-[#c3a66d]">{w.destination}</div>
                <h3 className="font-serif text-3xl mt-2">{w.couple}</h3>
                <p className="text-[#d8d0bf] text-sm mt-2 italic font-serif">“{w.quote}”</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

/* --- TESTIMONIAL BAND --- */
const Testimonial = () => (
  <section className="bg-[#053e49] py-24 text-[#faf4ea]">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
      <SectionLabel align="center" dark>The best part of our work</SectionLabel>
      <div className="mt-10">
        <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#ffeabf]">“We didn’t have to worry.”</p>
      </div>
      <div className="hairline mt-10 max-w-xs mx-auto" />
      <div className="mt-12 grid md:grid-cols-3 gap-10">
        {TESTIMONIALS.map((t) => (
          <div key={t.couple}>
            <p className="text-[#e4ddc8] font-serif text-lg leading-relaxed">“{t.quote}”</p>
            <div className="mt-5 eyebrow text-[#c3a66d]">— {t.couple} · {t.place}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --- JOURNAL TEASE --- */
const JournalTease = () => (
  <section className="bg-[#fbf8f2] py-24">
    <div className="max-w-[1240px] mx-auto px-6 md:px-10 text-center">
      <SectionLabel align="center">Journal</SectionLabel>
      <Heading className="mt-5 font-serif">Plan Better. <span className="italic">Dream Bigger.</span></Heading>
      <div className="mt-14 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
        {[['Destination Guides','Where to marry'],['Planning Guides','How to prepare'],['Wedding Budgets','Where money goes'],['Venue Guides','What to look for'],['Real Weddings','How it came together'],['Wedding Pros','Expert advice']].map(([t,s]) => (
          <Link to="/journal" key={t} className="border border-[#e8e0d2] p-5 hover:border-[#c3a66d] hover:bg-white transition-colors text-left">
            <div className="font-serif text-lg text-[#053e49]">{t}</div>
            <div className="text-xs text-[#6b7c80] mt-2">{s}</div>
            <ChevronRight size={14} className="text-[#c3a66d] mt-3" />
          </Link>
        ))}
      </div>
      <Link to="/journal" className="btn-outline-dark mt-12">Visit the Journal <ArrowRight size={16} /></Link>
    </div>
  </section>
);

/* --- PRESS STRIP --- */
const Press = () => (
  <section className="bg-[#faf4ea] py-12 border-y border-[#e8e0d2] overflow-hidden">
    <div className="marquee-track">
      {[...PRESS, ...PRESS].map((p, i) => (
        <div key={`${p}-${i}`} className="px-10 flex items-center gap-10 text-[#8a7f66]">
          <span className="font-serif text-xl md:text-2xl italic opacity-80">{p}</span>
          <span className="text-[#c3a66d]">❖</span>
        </div>
      ))}
    </div>
  </section>
);

/* --- CONTACT TEASE --- */
const ContactTease = () => (
  <section className="bg-[#faf4ea] py-28" id="contact">
    <div className="max-w-[1100px] mx-auto px-6 md:px-10">
      <div className="text-center max-w-2xl mx-auto">
        <SectionLabel align="center">Start Planning</SectionLabel>
        <Heading className="mt-5 font-serif">Start Planning <span className="italic">Your Wedding</span></Heading>
        <p className="mt-6 text-[#4a5c60]">Tell us about your celebration. A Sunday Mirror wedding specialist will be in touch.</p>
      </div>
      <div className="mt-14"><ContactForm /></div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Trust />
      <Responsibility />
      <Why />
      <Destinations />
      <Finder />
      <ServicesSec />
      <FindTeam />
      <Certified />
      <HowItWorks />
      <MirrorAI />
      <RealWeddings />
      <Testimonial />
      <Press />
      <JournalTease />
      <CTABanner />
      <ContactTease />
    </main>
  );
}
